/**
 * A trip drawn as the line it is.
 *
 * A journey is a list of places in order, and a table of them answers "which
 * stops are there" while the question people actually have is "where has it
 * got to". So it is drawn as a line: stops behind it filled in, the one it is
 * standing at ringed, the ones ahead hollow, and the gap it is currently
 * crossing dashed.
 */
import { Check, MapPin, Truck } from 'lucide-react';
import type { JourneyRow, JourneyStopRow } from '@mouldtrack/shared';
import { cn } from '../../lib/cn.js';

function when(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

/** What a stop says about itself, under its name. */
function stopDetail(stop: JourneyStopRow): string {
  if (stop.state === 'DEPARTED') {
    return stop.departedAt ? `left ${when(stop.departedAt)}` : 'left';
  }
  if (stop.state === 'ARRIVED') {
    return stop.arrivedAt ? `arrived ${when(stop.arrivedAt)}` : 'arrived';
  }
  return stop.kind === 'DESTINATION' ? 'final destination' : 'not yet reached';
}

export function JourneyTimeline({ journey }: { journey: JourneyRow }) {
  const stops = journey.stops;

  return (
    <ol className="relative flex flex-col gap-0">
      {stops.map((stop, i) => {
        const done = stop.state === 'DEPARTED';
        const here = stop.state === 'ARRIVED';
        const next = stops[i + 1];
        // The leg after this stop is being crossed right now when the tool has
        // left here and not yet reached the next place.
        const crossing = done && next != null && next.state === 'PENDING' && journey.inTransit;

        return (
          <li key={stop.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-[10px] font-semibold',
                  done && 'border-success bg-success text-white',
                  here && 'border-primary bg-primary-subtle text-primary',
                  !done && !here && 'border-border bg-surface text-muted-foreground',
                )}
                aria-hidden
              >
                {done ? <Check className="size-3.5" strokeWidth={3} /> : i + 1}
              </span>
              {i < stops.length - 1 ? (
                <span
                  className={cn(
                    'w-0.5 flex-1',
                    crossing
                      ? 'bg-[repeating-linear-gradient(to_bottom,var(--color-warning)_0_4px,transparent_4px_8px)]'
                      : done
                        ? 'bg-success'
                        : 'bg-border',
                  )}
                  style={{ minHeight: crossing ? 38 : 26 }}
                  aria-hidden
                />
              ) : null}
            </div>

            <div className={cn('min-w-0 pb-3', i === stops.length - 1 && 'pb-0')}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className={cn('font-medium', here && 'text-primary')}>
                  {stop.location.name || stop.location.code}
                </span>
                {stop.kind !== 'CHECKPOINT' ? (
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    {stop.kind === 'START' ? 'start' : 'destination'}
                  </span>
                ) : null}
                {here ? (
                  <span className="inline-flex items-center gap-1 text-xs text-primary">
                    <MapPin className="size-3" aria-hidden /> here now
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-muted-foreground">{stopDetail(stop)}</p>

              {crossing ? (
                <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-warning-subtle px-2 py-1 text-xs text-warning-foreground">
                  <Truck className="size-3.5" aria-hidden />
                  on the road to {next?.location.name || next?.location.code}
                </p>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
